import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
    Drawer, Box, Typography, Stack, TextField, Button,
    Divider, IconButton, Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded';
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ApiClient from '../../../common/API';
import { useQueryClient } from 'react-query';
import { useGroupList } from '../../../common/API/hooks';
import { useSave } from '../utils';
import { getSelectedOption } from '../../../common/utils';
import DropdownCheckboxV3 from '../../../common/components/DropdownCheckboxV3';
import ConfirmationModal from '../../../common/components/ConfirmationModal';
import handleError from '../ErrorHandling';

const BRAND = '#0F2FEB';
const EMPTY_USER = { adid: '', firstName: '', lastName: '', email: '', groups: [] };

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function UserDrawer({ isOpen, mode, user, onClose }) {
    const queryClient = useQueryClient();
    const { data: groupList, isLoading: groupLoading } = useGroupList();
    const { saveData: saveUsers } = useSave();

    const [form, setForm] = useState(EMPTY_USER);
    const [hasChanges, setHasChanges] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [showWarning, setShowWarning] = useState(false);
    const [saving, setSaving] = useState(false);

    const isEdit = mode === 'edit';

    // Sync form state whenever the drawer opens or the target user changes
    useEffect(() => {
        if (isOpen) {
            if (isEdit && user) {
                setForm({
                    adid: user.adid || '',
                    firstName: user.first_name || '',
                    lastName: user.last_name || '',
                    email: user.email || '',
                    groups: user.groups || [],
                });
            } else {
                setForm(EMPTY_USER);
            }
            setHasChanges(false);
            setEmailError('');
        }
    }, [isOpen, user, isEdit]);

    const groupOptions = React.useMemo(() => {
        if (groupLoading || !groupList) return [];
        return groupList.map(g => ({ value: g.name, label: g.name, key: g.id }));
    }, [groupList, groupLoading]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setHasChanges(true);
        setForm(prev => ({ ...prev, [name]: value }));
        if (name === 'email') {
            setEmailError(value && !emailPattern.test(value) ? 'Invalid email address' : '');
        }
    };

    const handleGroupsChange = (_, newValue) => {
        setHasChanges(true);
        setForm(prev => ({ ...prev, groups: newValue || [] }));
    };

    const handleClose = () => {
        if (hasChanges) setShowWarning(true);
        else onClose();
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            if (isEdit) {
                await ApiClient().patch(`user/${user.id}/`, {
                    adid: form.adid,
                    first_name: form.firstName,
                    last_name: form.lastName,
                    groups: form.groups,
                });
                queryClient.invalidateQueries(['user']);
                toast.success('User updated successfully!');
            } else {
                await saveUsers('user', {
                    adid: form.adid,
                    first_name: form.firstName,
                    last_name: form.lastName,
                    username: form.email,
                    groups: form.groups,
                });
                toast.success('User added successfully!');
            }
            onClose();
        } catch (error) {
            toast.error(handleError(error, 'User', isEdit ? 'edit' : 'save'));
        } finally {
            setSaving(false);
        }
    };

    const canSave = form.adid && form.firstName && form.lastName &&
        (isEdit || (form.email && !emailError));

    return (
        <>
            <Drawer
                anchor="right"
                open={isOpen}
                onClose={handleClose}
                PaperProps={{
                    sx: { width: { xs: '100%', sm: 480 }, display: 'flex', flexDirection: 'column' },
                }}
            >
                {/* Header */}
                <Box sx={{ px: 3, py: 2.5, bgcolor: BRAND, color: 'white', flexShrink: 0 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                            {isEdit
                                ? <EditNoteRoundedIcon sx={{ fontSize: '1.6rem' }} />
                                : <PersonAddAlt1RoundedIcon sx={{ fontSize: '1.6rem' }} />}
                            <Typography variant="h6" fontWeight="bold">
                                {isEdit ? 'Edit User' : 'Add New User'}
                            </Typography>
                        </Stack>
                        <IconButton onClick={handleClose} size="small" sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Stack>
                    {isEdit && user && (
                        <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5, ml: 4 }}>
                            {user.first_name} {user.last_name} &bull; {user.email}
                        </Typography>
                    )}
                </Box>

                <Divider />

                {/* Scrollable Form Body */}
                <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 3 }}>
                    <Stack spacing={2.5}>
                        <TextField
                            label="ADID / Network Username"
                            name="adid"
                            size="small"
                            fullWidth
                            required
                            autoFocus
                            value={form.adid}
                            onChange={handleChange}
                        />

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                                label="First Name"
                                name="firstName"
                                size="small"
                                fullWidth
                                required
                                value={form.firstName}
                                onChange={handleChange}
                            />
                            <TextField
                                label="Last Name"
                                name="lastName"
                                size="small"
                                fullWidth
                                required
                                value={form.lastName}
                                onChange={handleChange}
                            />
                        </Stack>

                        <TextField
                            label="Email Address"
                            name="email"
                            type="email"
                            size="small"
                            fullWidth
                            required={!isEdit}
                            disabled={isEdit}
                            value={form.email}
                            onChange={handleChange}
                            error={!!emailError}
                            helperText={emailError || (isEdit ? 'Email cannot be changed here.' : '')}
                        />

                        <Box>
                            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                                Groups
                            </Typography>
                            <DropdownCheckboxV3
                                options={groupOptions}
                                label="Select Group(s)"
                                placeholder="Select Group(s)"
                                limitTags={2}
                                loading={groupLoading}
                                onChange={handleGroupsChange}
                                value={getSelectedOption(form.groups, groupOptions) || []}
                            />
                        </Box>

                        {isEdit && (
                            <Alert severity="info" variant="outlined" sx={{ fontSize: '0.8rem' }}>
                                To change a user's email address, contact your system administrator.
                            </Alert>
                        )}
                    </Stack>
                </Box>

                <Divider />

                {/* Sticky Footer */}
                <Box sx={{ px: 3, py: 2, flexShrink: 0 }}>
                    <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                        <Button
                            variant="outlined"
                            onClick={handleClose}
                            sx={{ textTransform: 'none', color: BRAND, borderColor: BRAND, minWidth: 90 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={!canSave || saving}
                            sx={{
                                textTransform: 'none', bgcolor: BRAND, minWidth: 110, fontWeight: 600,
                                '&:hover': { bgcolor: '#0A22C0' },
                            }}
                        >
                            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add User'}
                        </Button>
                    </Stack>
                </Box>
            </Drawer>

            {showWarning && (
                <ConfirmationModal
                    isOpen={showWarning}
                    onClose={() => setShowWarning(false)}
                    onConfirm={() => { setShowWarning(false); onClose(); }}
                    icon={<WarningAmberIcon sx={{ fontSize: '60px' }} color="warning" />}
                    title="Discard Changes?"
                    message="You have unsaved changes. Are you sure you want to close without saving?"
                />
            )}
        </>
    );
}

